/**
 * URL-native compare set (ADR-0044 · #83).
 *
 * A user-chosen ticker list that travels in the URL (`cmp=`) and on the API
 * (`compare=`). Orthogonal to `FILTER_KEYS`: those stay byte-identical to the
 * `market.index_code` PG enum labels. `?idx=SPX&cmp=AAPL,MSFT` is "these two
 * S&P names", and the two compose.
 *
 * The opposite of `book` on the privacy axis. Book is a FLAG because putting
 * holdings in a shared link publishes them into history and referrers.
 * Compare IS the research object: the tickers have to be in the link or a
 * pasted URL cannot restore the screen.
 *
 * Unknown or delisted members 404/410 the request (ADR-0012). This schema only
 * validates SHAPE — identity is the repository's job, because `contracts`
 * cannot see the universe.
 */
import { z } from 'zod';

import { RequestedTickerSchema, type Ticker } from './primitives';

/**
 * Compare is a research set, not a portfolio. 32 stays under MATRIX_MAX (64)
 * so a later correlation view can take the same list without a second cap,
 * and 32 × 17-char tokens stay well inside shareable URL length.
 */
export const COMPARE_SET_MAX = 32;

export type CompareSet = Ticker[];

function partsFrom(value: unknown): readonly string[] | undefined {
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
  }
  if (!Array.isArray(value)) return undefined;
  const parts: string[] = [];
  for (const item of value) {
    if (typeof item !== 'string' && typeof item !== 'number') return undefined;
    const part = String(item).trim();
    if (part.length > 0) parts.push(part);
  }
  return parts;
}

/**
 * URL/API preprocess. Empty / omitted → `undefined` (filter off). A present
 * list is tokenized only; ticker branding, uniqueness and the cap live on
 * `CompareSetSchema` so a hostile value cannot skip them by arriving pre-split.
 */
export function compareSetInput(value: unknown): unknown {
  if (value === undefined || value === null || value === '') return undefined;
  const parts = partsFrom(value);
  return parts === undefined ? value : parts.length === 0 ? undefined : parts;
}

function uniqueFirstWins(tickers: readonly Ticker[]): Ticker[] {
  const seen = new Set<Ticker>();
  const out: Ticker[] = [];
  for (const ticker of tickers) {
    if (seen.has(ticker)) continue;
    seen.add(ticker);
    out.push(ticker);
  }
  return out;
}

/**
 * Non-empty, capped, first-wins unique. Unique runs BEFORE the cap so
 * `AAPL` repeated 40 times is one name, not a validation failure — the cap
 * bounds distinct research names, not token count.
 */
export const CompareSetSchema = z
  .array(RequestedTickerSchema)
  .min(1)
  .transform(uniqueFirstWins)
  .refine((tickers) => tickers.length <= COMPARE_SET_MAX);

/** Write path: one comma-joined token so the URL stays `cmp=AAPL,MSFT`. */
export function formatCompareSet(tickers: CompareSet): string {
  return tickers.join(',');
}

/** React Query cache slot: omitted compare occupies one stable `null`. */
export function compareCacheSlot(compare: CompareSet | undefined): string | null {
  return compare === undefined ? null : formatCompareSet(compare);
}
